import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

interface AccessTokenContextValue {
  isAccessTokenReady: boolean;
  setAccessTokenReady: (isReady: boolean) => void;
}

const AccessTokenContext = createContext<AccessTokenContextValue>({
  isAccessTokenReady: false,
  setAccessTokenReady: () => {},
});

export function AccessTokenProvider({ children }: { children: React.ReactNode }) {
  const [isAccessTokenReady, setIsReady] = useState(false);
  const setAccessTokenReady = useCallback((isReady: boolean) => setIsReady(isReady), []);

  const value = useMemo(
    () => ({ isAccessTokenReady, setAccessTokenReady }),
    [isAccessTokenReady, setAccessTokenReady],
  );

  return <AccessTokenContext.Provider value={value}>{children}</AccessTokenContext.Provider>;
}

export function useAccessTokenReady() {
  return useContext(AccessTokenContext);
}

/**
 * True once we know who the user is: either the axios Authorization header has been set, or Auth0
 * has settled on nobody being logged in.
 *
 * Routes used to get this by calling useAccessToken() on every route and throwing the token away.
 * That call produced the single largest error group in production, because it fetches a token even
 * on public pages — and it only ever served as a timing barrier for the analytics POST, which
 * reads the header that useConfigureAccessToken sets.
 */
export function useIsAuthSettled() {
  const { isAuthenticated, isLoading } = useAuth0();
  const { isAccessTokenReady } = useAccessTokenReady();

  if (isLoading) return false;
  return isAuthenticated ? isAccessTokenReady : true;
}
